import {Component, Inject, OnDestroy} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {ECaseSnackBarService, ECaseUtils, ExplorerService} from 'synto-common';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {DocumentItem} from '../document-list.models';

export interface DocumentRenameDialogData {
  row: DocumentItem;
  screenName: string;
}

@Component({
  selector: 'app-document-rename-dialog',
  templateUrl: './document-rename-dialog.component.html',
  styleUrls: ['./document-rename-dialog.component.scss']
})
export class DocumentRenameDialogComponent implements OnDestroy {

  suffix = '';
  maxBaseLength = 200;
  submitting = false;
  renameForm: FormGroup;
  private readonly currentName: string;
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<DocumentRenameDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentRenameDialogData,
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private explorerService: ExplorerService,
    private eCaseSnackBarService: ECaseSnackBarService
  ) {
    this.currentName = this.data?.row?.fileName || '';
    const dot = this.currentName.lastIndexOf('.');
    this.suffix = dot < 0 ? '' : this.currentName.substring(dot);
    this.maxBaseLength = 200 - this.suffix.length;
    const base = dot < 0 ? this.currentName : this.currentName.substring(0, dot);
    this.renameForm = this.formBuilder.group({
      newFileName: [base, [Validators.required, this.differentNameValidator]]
    });
  }

  private fullNameOf(base: string): string {
    const value = (base || '').trim();
    return this.suffix && value.endsWith(this.suffix) ? value : value + this.suffix;
  }

  differentNameValidator: ValidatorFn = (control: FormControl) => {
    const value = (control.value || '').trim();
    if (!value) {
      return {required: true};
    }
    return this.fullNameOf(value) === this.currentName ? {sameName: true} : null;
  };

  onConfirm(): void {
    if (this.renameForm.invalid || this.submitting) {
      return;
    }
    const edmRepositoryDocumentId = this.data?.row?.edmRepositoryDocumentId;
    if (!edmRepositoryDocumentId) {
      this.eCaseSnackBarService.show('failure',
        ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.rename.error'));
      return;
    }
    const newFileName = this.fullNameOf(this.renameForm.controls['newFileName'].value);
    this.submitting = true;
    this.dialogRef.disableClose = true;
    this.explorerService.renameDocument(edmRepositoryDocumentId, newFileName, this.data.screenName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.eCaseSnackBarService.show('success',
            ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.rename.success'));
          this.close(true);
        },
        error: (error: any) => {
          const message = error?.error?.errorMessage || error?.error?.message || error?.errorMessage
            || 'ecase.explorer.documentList.rename.error';
          this.eCaseSnackBarService.show('failure',
            ECaseUtils.getTranslatedValueFromKey(this.translate, message));
          this.submitting = false;
          this.dialogRef.disableClose = false;
        }
      });
  }

  onClose(): void {
    if (!this.submitting) {
      this.dialogRef.close(false);
    }
  }

  private close(result: boolean): void {
    this.submitting = false;
    this.dialogRef.disableClose = false;
    this.dialogRef.close(result);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}